// SHFT-5757: shared, framework-free helpers for the internal-build environment
// switcher. Deliberately has zero Angular dependency so it can be imported
// from src/main.ts and executed BEFORE platformBrowserDynamic().bootstrapModule()
// runs (i.e. before Angular's DI/module system exists). The same functions are
// reused from the login page's "change environment" link post-bootstrap.

export const INTERNAL_ENV_STORAGE_KEY = 'eshyft-fm-internal-selected-env';

export interface EnvironmentDirectoryEntry {
  baseUrl: string;
  webHost: string;
  firebaseTenantId: string;
}

export type EnvironmentDirectory = Record<string, EnvironmentDirectoryEntry>;

/**
 * Remote Config publishes `environment_directory` with snake_case keys
 * (base_url/web_host/firebase_tenant_id - the naming convention shared with
 * the Flutter apps), so raw parsed JSON must be normalized into this
 * interface's camelCase shape before use. Returns null if nothing usable
 * could be extracted, so callers can fall back to bundled defaults.
 */
export function normalizeEnvironmentDirectory(raw: unknown): EnvironmentDirectory | null {
  if (!raw || typeof raw !== 'object') {
    return null;
  }
  const normalized: EnvironmentDirectory = {};
  for (const [key, value] of Object.entries(raw as Record<string, any>)) {
    if (!value || typeof value !== 'object') {
      continue;
    }
    const baseUrl = value.baseUrl ?? value.base_url;
    const firebaseTenantId = value.firebaseTenantId ?? value.firebase_tenant_id;
    if (!baseUrl || !firebaseTenantId) {
      continue;
    }
    normalized[key] = { baseUrl, webHost: value.webHost ?? value.web_host ?? '', firebaseTenantId };
  }
  return Object.keys(normalized).length ? normalized : null;
}

/**
 * This app's `socketUrl` has always been derivable from `apiUrl` by dropping
 * the trailing `/api/v2` path (true for every existing environment.*.ts file,
 * local dev included) - confirmed empirically rather than assumed. The
 * `environment_directory` Remote Config parameter only publishes `base_url`
 * (this app's `apiUrl`), so socketUrl is derived here rather than sourced
 * separately.
 */
export function deriveSocketUrl(baseUrl: string): string {
  return baseUrl.replace(/\/api\/v2\/?$/, '');
}

/** Reads the persisted environment key, defaulting to "dev" if unset or unknown. */
export function getPersistedEnvKey(directory: EnvironmentDirectory): string {
  let stored: string | null = null;
  try {
    stored = localStorage.getItem(INTERNAL_ENV_STORAGE_KEY);
  } catch {
    stored = null;
  }
  if (stored && directory[stored]) {
    return stored;
  }
  if (directory['dev']) {
    return 'dev';
  }
  return Object.keys(directory)[0];
}

export function persistEnvKey(key: string): void {
  try {
    localStorage.setItem(INTERNAL_ENV_STORAGE_KEY, key);
  } catch {
    // Storage unavailable (e.g. private mode) - the picker will just reappear next load.
  }
}

/** Mutates the running `environment` object in place with the resolved directory entry. */
export function applyEnvironmentEntry(
  target: { apiUrl: string; socketUrl: string; firebaseTenantId: string },
  entry: EnvironmentDirectoryEntry
): void {
  target.apiUrl = entry.baseUrl;
  target.socketUrl = deriveSocketUrl(entry.baseUrl);
  target.firebaseTenantId = entry.firebaseTenantId;
}

/**
 * Renders a blocking, full-screen picker overlay directly against the DOM
 * (no Angular component - Angular hasn't bootstrapped yet when this is called
 * from main.ts). Resolves with the chosen environment key once the user hits
 * "Continue". Does not persist or apply the choice itself - callers do that.
 */
export function showEnvPickerOverlay(directory: EnvironmentDirectory, selectedKey: string): Promise<string> {
  return new Promise(resolve => {
    const overlay = document.createElement('div');
    overlay.setAttribute(
      'style',
      [
        'position:fixed',
        'inset:0',
        'z-index:2147483647',
        'display:flex',
        'align-items:center',
        'justify-content:center',
        'background:rgba(15,17,21,0.85)',
        'font-family:-apple-system,BlinkMacSystemFont,Segoe UI,Roboto,Helvetica,Arial,sans-serif'
      ].join(';')
    );

    const card = document.createElement('div');
    card.setAttribute(
      'style',
      [
        'background:#fff',
        'border-radius:8px',
        'padding:24px 28px',
        'min-width:280px',
        'max-width:90vw',
        'box-shadow:0 8px 30px rgba(0,0,0,0.35)'
      ].join(';')
    );

    const title = document.createElement('div');
    title.textContent = 'ESHYFT internal build';
    title.setAttribute('style', 'font-size:16px;font-weight:600;margin-bottom:4px;color:#111;');

    const subtitle = document.createElement('div');
    subtitle.textContent = 'Choose which backend this session should point at.';
    subtitle.setAttribute('style', 'font-size:13px;color:#555;margin-bottom:16px;');

    const select = document.createElement('select');
    select.setAttribute('style', 'width:100%;padding:8px;font-size:14px;margin-bottom:16px;box-sizing:border-box;');
    Object.keys(directory)
      .sort()
      .forEach(key => {
        const option = document.createElement('option');
        option.value = key;
        option.textContent = key;
        if (key === selectedKey) {
          option.selected = true;
        }
        select.appendChild(option);
      });

    const button = document.createElement('button');
    button.textContent = 'Continue';
    button.setAttribute(
      'style',
      [
        'width:100%',
        'padding:10px',
        'font-size:14px',
        'font-weight:600',
        'background:#1a73e8',
        'color:#fff',
        'border:none',
        'border-radius:4px',
        'cursor:pointer'
      ].join(';')
    );
    button.addEventListener('click', () => {
      const chosen = select.value;
      overlay.remove();
      resolve(chosen);
    });

    card.appendChild(title);
    card.appendChild(subtitle);
    card.appendChild(select);
    card.appendChild(button);
    overlay.appendChild(card);
    document.body.appendChild(overlay);
  });
}
